import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { FileText, Users, AlertTriangle, TrendingUp, Clock, CheckCircle, XCircle, RefreshCw, Eye } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format, differenceInDays, parseISO } from 'date-fns';
import { estaVencida, fmtPatron } from '../utils/dates';
import { es } from 'date-fns/locale';

const STATUS_LABEL = { active: 'Vigente', endorsed: 'Endosada', cancelled: 'Cancelada', expired: 'Vencida' };
const STATUS_COLOR = { active: 'var(--accent)', endorsed: 'var(--blue)', cancelled: 'var(--red)', expired: 'var(--amber)' };

export default function DashboardPage() {
  const { clients, policies, companies, currentUser, debts, claims, tickets, employees } = useApp();
  const navigate = useNavigate();
  const today = new Date();

  const active = policies.filter(p => p.status === 'active' || p.status === 'endorsed');
  const cancelled = policies.filter(p => p.status === 'cancelled');
  const expiringSoon = active.filter(p => {
    const days = differenceInDays(parseISO(p.endDate), today);
    return days >= 0 && days <= 30;
  });

  // By company — se arma desde las PÓLIZAS activas reales (agrupando por su
  // campo `company`), no desde la tabla `companies`. Antes solo contaba las
  // compañías dadas de alta en el sistema, así que pólizas de una compañía no
  // registrada (o con el nombre escrito distinto) no sumaban a ninguna barra.
  const byCompany = Object.entries(
    active.reduce((acc, p) => {
      const nombre = (p.company || '').trim() || 'Sin compañía';
      acc[nombre] = (acc[nombre] || 0) + 1;
      return acc;
    }, {})
  )
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  // By status pie
  const pieData = [
    { name: 'Vigentes', value: active.length, color: 'var(--accent)' },
    { name: 'Canceladas', value: cancelled.length, color: 'var(--red)' },
  ].filter(d => d.value > 0);

  // Recent policies
  const recent = [...policies].reverse().slice(0, 5);

  // Commission estimate for admin
  const totalCommission = active.reduce((acc, p) => {
    const co = companies.find(c => c.name === p.company);
    return acc + (p.premium || 0) * ((co?.commissionRate || 0) / 100);
  }, 0);

  // ── CENTRO DE ALERTAS (lo que necesita atención hoy) ──────────────────────
  const todayStr = format(today, 'yyyy-MM-dd');

  const renovacionesPend = active.filter(p => p.fromRenewalNotice);
  const vencenSemana = active.filter(p => {
    if (p.fromRenewalNotice || !p.endDate) return false;
    const d = differenceInDays(parseISO(p.endDate), today);
    return d >= 0 && d <= 7;
  });
  const deudoresPend = (debts || []).filter(d => d.status === 'pending');
  // Una cuota está vencida si su fecha (normalizada a Date real) es anterior a
  // hoy. Comparábamos strings (d.dueDate < todayStr), pero eso solo funciona si
  // la fecha está en ISO yyyy-MM-dd; las deudas importadas con dueDate en otro
  // formato (dd/MM/yyyy) o vacío rompían la cuenta y no aparecían en el alerta.
  const deudoresVencidos = deudoresPend.filter(d => estaVencida(d.dueDate));
  const siniestrosAbiertos = (claims || []).filter(c => c.status && c.status !== 'closed' && c.status !== 'resolved');
  const siniestrosAlerta = (claims || []).filter(c => c.reminder && c.reminder <= todayStr && c.status !== 'closed');
  const ticketsSinAsignar = (tickets || []).filter(t => t.status !== 'closed' && !t.assignedToId);
  const ticketsAbiertos = (tickets || []).filter(t => t.status && t.status !== 'closed');
  // Solicitudes de licencia pendientes de aprobar (acción del CEO)
  const licenciasPend = (employees || []).reduce((acc, e) =>
    acc + ((e.licenses || []).filter(l => l.status === 'pendiente').length), 0);

  const alertas = [];
  if (renovacionesPend.length) alertas.push({ tono: 'amber', icon: RefreshCw, texto: `${renovacionesPend.length} renovación(es) por gestionar`, link: '/renewals' });
  if (vencenSemana.length) alertas.push({ tono: 'amber', icon: Clock, texto: `${vencenSemana.length} póliza(s) vencen esta semana`, link: '/policies' });
  if (deudoresVencidos.length) alertas.push({ tono: 'red', icon: AlertTriangle, texto: `${deudoresVencidos.length} deudor(es) con cuota vencida`, link: '/debts' });
  else if (deudoresPend.length) alertas.push({ tono: 'amber', icon: AlertTriangle, texto: `${deudoresPend.length} deudor(es) pendiente(s)`, link: '/debts' });
  if (siniestrosAlerta.length) alertas.push({ tono: 'red', icon: AlertTriangle, texto: `${siniestrosAlerta.length} siniestro(s) con recordatorio vencido`, link: '/claims' });
  else if (siniestrosAbiertos.length) alertas.push({ tono: 'blue', icon: FileText, texto: `${siniestrosAbiertos.length} siniestro(s) abierto(s)`, link: '/claims' });
  if (ticketsSinAsignar.length) alertas.push({ tono: 'amber', icon: AlertTriangle, texto: `${ticketsSinAsignar.length} ticket(s) sin asignar`, link: '/tickets' });
  else if (ticketsAbiertos.length) alertas.push({ tono: 'blue', icon: FileText, texto: `${ticketsAbiertos.length} ticket(s) abierto(s)`, link: '/tickets' });
  if (licenciasPend > 0) alertas.push({ tono: 'amber', icon: Clock, texto: `${licenciasPend} solicitud(es) de licencia por aprobar`, link: '/licenses' });

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            {format(today, "EEEE d 'de' MMMM yyyy", { locale: es })}
            {' · '}Bienvenido, <strong>{currentUser?.name}</strong>
          </p>
        </div>
      </div>

      {/* ── CENTRO DE ALERTAS: lo que necesita tu atención hoy ── */}
      {alertas.length > 0 ? (
        <div className="card" style={{ marginBottom: 20, borderLeft: '3px solid var(--amber)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <AlertTriangle size={16} color="var(--amber)" />
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, margin: 0 }}>Requiere tu atención</h3>
            <span className="badge" style={{ background: 'var(--amber-dim)', color: 'var(--amber)' }}>{alertas.length}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {alertas.map((a, i) => {
              const colores = {
                red: { c: 'var(--red)', bg: 'var(--red-dim)' },
                amber: { c: 'var(--amber)', bg: 'var(--amber-dim)' },
                blue: { c: 'var(--blue)', bg: 'var(--blue-dim, rgba(96,165,250,0.12))' },
              }[a.tono] || { c: 'var(--text-secondary)', bg: 'var(--bg-elevated)' };
              const Icon = a.icon;
              return (
                <button key={i} onClick={() => navigate(a.link)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: colores.bg, border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'transform 0.1s' }}
                  onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
                  onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                  <Icon size={18} color={colores.c} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{a.texto}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 20, borderLeft: '3px solid var(--accent)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <CheckCircle size={18} color="var(--accent)" />
          <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Todo al día. No hay nada urgente que atender ahora mismo.</span>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card" style={{ borderLeft: '3px solid var(--accent)' }}>
          <div className="stat-label"><Users size={11} style={{ display: 'inline', marginRight: 5 }} />Clientes</div>
          <div className="stat-value">{clients.length}</div>
          <div className="stat-sub">en cartera</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '3px solid var(--blue)' }}>
          <div className="stat-label"><FileText size={11} style={{ display: 'inline', marginRight: 5 }} />Pólizas activas</div>
          <div className="stat-value">{active.length}</div>
          <div className="stat-sub">de {policies.length} totales</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '3px solid var(--amber)' }}>
          <div className="stat-label"><Clock size={11} style={{ display: 'inline', marginRight: 5 }} />Vencen en 30 días</div>
          <div className="stat-value" style={{ color: expiringSoon.length > 0 ? 'var(--amber)' : undefined }}>{expiringSoon.length}</div>
          <div className="stat-sub">requieren renovación</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '3px solid var(--red)' }}>
          <div className="stat-label"><XCircle size={11} style={{ display: 'inline', marginRight: 5 }} />Canceladas</div>
          <div className="stat-value">{cancelled.length}</div>
          <div className="stat-sub">prospectos para IA</div>
        </div>
        {currentUser?.role === 'admin' && (
          <div className="stat-card" style={{ borderLeft: '3px solid var(--amber)', background: 'rgba(245,166,35,0.05)' }}>
            <div className="stat-label" style={{ color: 'var(--amber)' }}>💰 Comisión estimada</div>
            <div className="stat-value" style={{ color: 'var(--amber)', fontSize: 22 }}>
              ${totalCommission.toLocaleString('es-UY', { maximumFractionDigits: 0 })}
            </div>
            <div className="stat-sub">sobre pólizas activas</div>
          </div>
        )}
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 28 }}>
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, marginBottom: 20, color: 'var(--text-secondary)' }}>PÓLIZAS POR COMPAÑÍA</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={byCompany} margin={{ left: -20 }}>
              <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 11, fontFamily: 'var(--font-display)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'rgba(124,108,248,0.05)' }} />
              <Bar dataKey="count" fill="var(--accent)" radius={[4, 4, 0, 0]} name="Pólizas" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--text-secondary)', alignSelf: 'flex-start' }}>ESTADO GENERAL</h3>
          <PieChart width={160} height={140}>
            <Pie data={pieData} cx={80} cy={70} innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
              {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
            </Pie>
            <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
          </PieChart>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
            {pieData.map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                <span style={{ color: 'var(--text-secondary)' }}>{d.name}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 600, fontFamily: 'var(--font-display)' }}>{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Expiring soon */}
      {expiringSoon.length > 0 && (
        <div className="card" style={{ marginBottom: 20, borderColor: 'rgba(245,166,35,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <AlertTriangle size={16} color="var(--amber)" />
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--amber)' }}>VENCIMIENTOS PRÓXIMOS (30 días)</h3>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Cliente</th><th>N° Póliza</th><th>Compañía</th><th>Vence</th><th>Días</th><th></th></tr></thead>
              <tbody>
                {expiringSoon.map(p => {
                  const client = clients.find(c => c.id === p.clientId) || {};
                  const days = differenceInDays(parseISO(p.endDate), today);
                  return (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 500 }}>{client.name}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{p.number}</td>
                      <td>{p.company}</td>
                      <td>{fmtPatron(p.endDate, 'dd/MM/yyyy')}</td>
                      <td><span className="badge badge-amber">{days}d</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/policies/${p.id}`)}><Eye size={12} /> Ver</button>
                          <button className="btn btn-amber btn-sm" onClick={() => navigate(`/policies/${p.id}?renovar=1`)}><RefreshCw size={12} /> Renovar</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent activity */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>ÚLTIMAS PÓLIZAS</h3>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/policies')}>Ver todas</button>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Cliente</th><th>N° Póliza</th><th>Tipo</th><th>Compañía</th><th>Estado</th></tr></thead>
            <tbody>
              {recent.map(p => {
                const client = clients.find(c => c.id === p.clientId) || {};
                return (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/policies/${p.id}`)}>
                    <td style={{ fontWeight: 500 }}>{client.name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{p.number}</td>
                    <td style={{ textTransform: 'capitalize' }}>{p.type === 'auto' ? '🚗 Auto' : p.type === 'home' ? '🏠 Inmueble' : '📄 ' + p.type}</td>
                    <td>{p.company}</td>
                    <td><span className="badge" style={{ background: p.status === 'active' ? 'var(--accent-dim)' : p.status === 'cancelled' ? 'var(--red-dim)' : 'var(--blue-dim)', color: STATUS_COLOR[p.status] }}>{STATUS_LABEL[p.status]}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
